import React, { FC, useRef, useState } from 'react';
import Carousel from 'nuka-carousel';
import { Text } from '@components/typography';
import ReactHtmlParser from '@utils/htmlParser';
import { JoykitIcon } from '@components/JoykitIcon';
import { getRootUrl } from '@utils/urlHelpers.util';
import { SpacingStack } from '@components/SpacingStack';
import { ChevronRightIcon } from '@images/ChevronRight';
import { CarouselIndicator } from '@components/Carousel';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import {
  Container,
  ImgContainer,
  StyledBox,
  TextContainer,
  Inactive,
  Active,
  CarouselWrap,
  CarouselIndicatorWrap,
  PreviousSlideButton,
  NextSlideButton,
  StyledTitleText,
  ProductGrid,
  ProductImage,
  ProductGridItem,
  StyledUserStory,
  StyledReadMore,
  DisplayOnMobile,
  DisplayAboveMobile,
} from './UserContentSection.styles';
import { USER_CONTENTS } from './UserContentSection.constants';
import UserContentModal from './components/UserContentModal/UserContentModal';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { useTranslation } from 'react-i18next';

export const UserContentSection: FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [expandedSlideIndex, setExpandedSlideIndex] = useState<number | null>(null);

  const handleActiveSlide = (value: number) => {
    setSlideIndex(value);
  };

  const { t } = useTranslation('userContentCarousel');

  return (
    <CarouselWrap ref={sectionRef} id="UserContentSection">
      <Carousel
        slideIndex={slideIndex}
        style={{ backgroundColor: '#fffbf7' }}
        afterSlide={(params) => setSlideIndex(params)}
        speed={500}
        renderCenterLeftControls={({ previousSlide }) => (
          <PreviousSlideButton aria-label="Previous Slide Button" onClick={previousSlide} id="registry-shop-prev-btn">
            <DisplayOnMobile>
              <JoykitIcon name="backChevron" color="mono1" size={16} />
            </DisplayOnMobile>
            <DisplayAboveMobile>
              <JoykitIcon name="backChevron" color="mono1" size={24} />
            </DisplayAboveMobile>
          </PreviousSlideButton>
        )}
        renderCenterRightControls={({ nextSlide }) => (
          <NextSlideButton aria-label="Next Slide Button" onClick={nextSlide} id="regsitry-shop-next-btn">
            <DisplayOnMobile>
              <JoykitIcon name="forwardChevron" color="mono1" size={16} />
            </DisplayOnMobile>
            <DisplayAboveMobile>
              <JoykitIcon name="forwardChevron" color="mono1" size={24} />
            </DisplayAboveMobile>
          </NextSlideButton>
        )}
        renderBottomCenterControls={null}
        wrapAround
      >
        {USER_CONTENTS.map((userContent, index) => (
          <Container key={userContent.title}>
            <LazyLoadWrapper>
              <ImgContainer
                src={cloudAssetUrl(userContent.mediaLink, { optimization: 'aggressive' })}
                aria-label={`${userContent.title}'s Wedding Registry`}
              />
            </LazyLoadWrapper>
            <TextContainer>
              <StyledBox paddingX={6} backgroundColor="yellow1">
                <SpacingStack spacing={6} marginBottom={6}>
                  <StyledTitleText color="neutra6" variant="hed40" tagName="span">
                    {ReactHtmlParser(`${userContent.title} Wedding Registry`)}
                  </StyledTitleText>
                  <StyledUserStory color="neutra5" variant="dek20" tagName="span">
                    {ReactHtmlParser(userContent.excerpt)}
                  </StyledUserStory>
                  <StyledReadMore role="button" onClick={() => setExpandedSlideIndex(index)}>
                    <Text
                      variant="dek20"
                      aria-label={userContent.title}
                      style={{ fontWeight: 'bold' }}
                      color="actionPurple3"
                      tagName="span"
                    >
                      {t('readMore')}
                    </Text>
                    <ChevronRightIcon />
                  </StyledReadMore>
                </SpacingStack>
                <ProductGrid>
                  {userContent?.registryProducts?.map((product, index) => (
                    <ProductGridItem
                      id={`${userContent.title}RegistryItem${index}`}
                      key={index}
                      forwardedAs="a"
                      aria-label={`${userContent.title}RegistryItem${index}`}
                      target="_blank"
                      href={getRootUrl(product?.shopUrl)}
                    >
                      <ProductImage
                        src={product.imageUrl}
                        alt={t('productImageAlt', { name: userContent.title })}
                        mobileResizeWidth={600}
                        optimization="aggressive"
                      />
                    </ProductGridItem>
                  ))}
                </ProductGrid>
              </StyledBox>
              <CarouselIndicatorWrap>
                <CarouselIndicator
                  tabIndex={slideIndex === index ? 0 : -1}
                  quantity={USER_CONTENTS.length}
                  dotsPerPage={USER_CONTENTS.length}
                  infinite={false}
                  activeIndex={slideIndex}
                  onDotSelected={handleActiveSlide}
                  renderDot={<Inactive />}
                  renderDotActive={<Active />}
                />
              </CarouselIndicatorWrap>
            </TextContainer>
          </Container>
        ))}
      </Carousel>
      {expandedSlideIndex !== null && (
        <UserContentModal
          isModalOpen={expandedSlideIndex !== null}
          handleClose={() => setExpandedSlideIndex(null)}
          userContent={USER_CONTENTS[expandedSlideIndex]}
        />
      )}
    </CarouselWrap>
  );
};
